/**
 * ProductRow — the listing's list-mode row, the counterpart to `ProductCard`.
 *
 * Like the `Skeleton` family, upstream has **no component for this**: it's inline
 * screen markup on the listing screen (`Dens N Dente Tailwind.dc.html:748-762`).
 * Extracted here so the app has one copy. Every fixed width, padding and icon size
 * below is the handoff's value verbatim.
 *
 * Note how deliberately different it is from `ProductCard` — don't "unify" them:
 *
 * | Slot  | Card                                   | Row                                       |
 * | ----- | -------------------------------------- | ----------------------------------------- |
 * | media | square, fills the card                 | 64px rounded thumb, bordered              |
 * | name  | 2-line clamp                           | **single line**, ellipsis                 |
 * | stock | `check_circle` 14px, own line          | `circle` **9px dot**, fixed 84px column   |
 * | price | `PriceTag`, 2xl, superscript $         | plain `font-bold text-md`, 104px right    |
 * | fav   | glass overlay in the media corner      | **38px bordered circle** in the strip     |
 * | add   | 44px round icon button                 | `Button size="sm"` with the text "Add"    |
 *
 * The price here is a **pre-formatted string**, not a number — the row's treatment is
 * plain bold text with the unit beneath it, not `PriceTag`'s superscript-symbol
 * layout, so there's nothing for `PriceTag` to do.
 *
 * ─── Deliberate divergences ────────────────────────────────────────────────────
 *
 * 1. `media` node instead of the handoff's icon placeholder, for the same reason as
 *    `ProductCard`: CLAUDE.md rule 2 wants `ProductImage`'s `onError` fallback, and
 *    `design-system/` may not import from `common`.
 * 2. Controlled `qty` / `onQty` — quantity is owned by `ListProduct`.
 * 3. Stacks below `md`. The reference row is desktop-only; its fixed columns
 *    (84 + 104 + 38 + stepper + button) need ~400px before the name gets any space.
 */
import React from "react";

import { AddToFavorite } from "./AddToFavorite.jsx";
import { Button } from "./Button.jsx";
import { STOCK } from "./constants";
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import { QuantityStepper } from "./QuantityStepper.jsx";

export function ProductRow({
  name,
  brand,
  sku,
  media,
  price,
  unit,
  stock = "in",
  qty: qtyProp,
  onQty,
  favorite = false,
  onFavorite,
  onAdd,
  onClick,
  disableWhenOut = false,
  style = {},
  className = "",
}) {
  const [internalQty, setInternalQty] = React.useState(1);
  const qty = qtyProp === undefined ? internalQty : qtyProp;
  const setQty = onQty || setInternalQty;
  const st = STOCK[stock] || STOCK.in;

  return (
    <div
      style={style}
      className={cx(
        "flex flex-wrap items-center gap-[var(--space-4)] bg-card border border-line-subtle shadow-sm rounded-lg py-[var(--space-3)] px-[var(--space-5)]",
        "[transition:box-shadow_var(--duration-fast),border-color_var(--duration-fast)] hover:shadow-sm hover:border-orange-200",
        className
      )}
    >
      <span
        className="w-[64px] h-[64px] shrink-0 overflow-hidden rounded-md bg-neutral-50 border border-line-subtle text-ink-3 inline-flex items-center justify-center"
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "default" }}
      >
        {media}
      </span>

      <div
        className="flex-1 min-w-[10rem]"
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "default" }}
      >
        {brand && (
          <div className="text-2xs font-bold tracking-[var(--tracking-wider)] uppercase text-[var(--text-accent)]">
            {brand}
          </div>
        )}
        <div className="font-semibold text-sm leading-[1.35] overflow-hidden text-ellipsis whitespace-nowrap">
          {name}
        </div>
        {sku && <div className="text-xs text-ink-3">{sku}</div>}
      </div>

      <div
        className="inline-flex shrink-0 items-center gap-[6px] text-xs font-semibold md:w-[84px]"
        style={{ color: st.color }}
      >
        <Icon name="circle" size={9} weight={400} fill />
        {st.label}
      </div>

      <div className="shrink-0 text-right md:w-[104px]">
        <div className="font-bold text-md">{price}</div>
        {unit && <div className="text-2xs text-ink-3">{unit}</div>}
      </div>

      {onFavorite && (
        <AddToFavorite
          saved={favorite}
          onToggle={onFavorite}
          size="md"
          className="!border border-line-subtle"
        />
      )}

      <QuantityStepper value={qty} onChange={setQty} min={1} />

      <Button
        size="sm"
        variant="primary"
        startIcon="add_shopping_cart"
        disabled={disableWhenOut && stock === "out"}
        onClick={(e) => {
          e.stopPropagation();
          if (onAdd) onAdd(qty);
        }}
      >
        Add
      </Button>
    </div>
  );
}
